import { Route } from "@/routes/service/$id";
import { ServiceWorkflow } from "@/modules/ServiceCore/components/ServiceWorkflow";
import { IntroSection as AutoTablesIntroSection } from "@/modules/ServiceCore/components/AutoTables";
import {
  IntroSection as AutoJamIntroSection,
  SelectTable,
} from "@/modules/ServiceCore/components/AutoJam";
import styles from "./Service.module.scss";
import { useEffect, useCallback } from "react";
import { type Stage, type StepContent } from "@/modules/ServiceCore/types";
import { useNavigate } from "@tanstack/react-router";
import { useToastStore } from "@/shared/stores/toastStore";
import { useServiceAccountStore } from "@/shared/stores/serviceAccountStore";
import { useServiceWorkflowStore } from "@/shared/stores/serviceWorkflowStore";

export function Service() {
  const { id } = Route.useParams();
  const { pId, stage, tableId } = Route.useSearch();
  const { showToast } = useToastStore();
  const navigate = useNavigate({ from: Route.fullPath });

  const { serviceAccount, fetchServiceAccount } = useServiceAccountStore();
  const { stage: currentStage, setWorkflowState } = useServiceWorkflowStore();

  const handleTokenAdded = useCallback(
    (newSupplierId: string) => {
      showToast("Токен добавлен, можете подключить сервис", "success");
      useServiceWorkflowStore.setState({ pId: newSupplierId });
      navigate({
        search: (prev) => ({ ...prev, pId: newSupplierId }),
        replace: true,
      });
    },
    [navigate, showToast],
  );

  useEffect(() => {
    if (id === "autotables" || id === "autojam") {
      setWorkflowState({
        serviceId: id,
        serviceType: id === "autotables" ? "auto_table" : "auto_jam",
        pId,
        initStage: stage as Stage,
        initTableId: tableId,
        onTokenAddedCallback: handleTokenAdded,
      });
    }
    return () => {
      setWorkflowState({
        serviceId: null,
        serviceType: null,
        pId: undefined,
        initStage: undefined,
        initTableId: undefined,
        onTokenAddedCallback: null,
      });
    };
  }, [id, pId, stage, tableId, setWorkflowState, handleTokenAdded]);

  useEffect(() => {
    const loadServiceAccount = async () => {
      try {
        await fetchServiceAccount();
      } catch (error) {
        console.error("Ошибка загрузки сервисного аккаунта:", error);
      }
    };
    if (id === "autotables" || id === "autojam") {
      loadServiceAccount();
    }
  }, [id, fetchServiceAccount]);

  const resolveBlockValue = useCallback(
    (block: StepContent) => {
      if (block.flag === "email") return serviceAccount?.email || "";
      return block.value || "";
    },
    [serviceAccount],
  );

  const handleCopy = (value: string) => {
    navigator.clipboard.writeText(value);
  };

  const copyProps = { resolveBlockValue, onCopy: handleCopy };

  const renderService = () => {
    if (id === "autotables") {
      return (
        <ServiceWorkflow
          renderIntro={(props) => (
            <AutoTablesIntroSection {...props} copyProps={copyProps} />
          )}
        />
      );
    }
    if (id === "autojam") {
      return (
        <ServiceWorkflow
          renderIntro={(props) => <AutoJamIntroSection {...props} />}
          renderStageSpecific={() => {
            if (currentStage === "select_table") {
              return <SelectTable />;
            }
            return null;
          }}
        />
      );
    }
    return <h2>Сервис {id} не найден</h2>;
  };

  return <div className={styles.servicePage}>{renderService()}</div>;
}
